/**
 * Pinout interno — o que acontece **dentro** de um componente.
 *
 * O pinout normal liga componentes entre si. O interno descreve a ligação de um
 * pino do componente com os circuitos internos dele: onde o sinal entra, por
 * onde passa lá dentro. Por isso **tudo aqui é escopado por componente**, não
 * por projeto — inclusive os catálogos, que são próprios: componente interno e
 * circuito interno existem dentro de um componente e não fora dele.
 *
 * Sem chicote, sem cor e sem conector: nada disso existe do lado de dentro. No
 * lugar entra a **ligação externa** — o `pinoutId`, que diz de qual pino do
 * componente esta linha interna sai.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { api, noProjeto, obter } from "./cliente"
import { catalogoPorProjeto } from "./query"
import type { ItemDeCatalogo } from "./catalogos"
import type { PaginaResponse, RestricaoResumo } from "./tipos"

export type PinoutInternoResponse = {
  id: number
  componenteId: number
  pinoutId: number
  componenteInternoId: number
  componenteInternoDescricao: string
  circuitoInternoId: number
  circuitoInternoDescricao: string
  pino: string
  obs: string
  arquivoOriginal: string
  tipo: number
  restricoes: RestricaoResumo[]
}

export type FiltroInterno = {
  pinoutIds?: string
  componentesInternos?: string
  circuitosInternos?: string
  pinos?: string
}

export type ItemInternoParaGravar = {
  componenteId: number
  pinoutInternoId: number
  pinoutId: number
  componenteInternoId: number
  componenteInternoCodigo: string
  componenteInternoDescricao: string
  circuitoInternoId: number
  circuitoInternoDescricao: string
  restricaoId: number
  restricaoDescricao: string
  pino: string
  obs: string
  arquivoOriginal: string
  tipo: number
}

export function usarPinoutsInternos(componenteId: number, filtro: FiltroInterno, pagina: number) {
  return useQuery({
    queryKey: ["internos", componenteId, filtro, pagina] as const,
    enabled: componenteId > 0,
    queryFn: () => obter<PaginaResponse<PinoutInternoResponse>>(
      "api/pinouts-internos", { componenteId, ...filtro, pagina }),
  })
}

export function usarGravarInternos(projetoId: number, componenteId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (pinouts: ItemInternoParaGravar[]) =>
      (await api.post<{ criados: number; ignorados: number; idsCriados?: number[] }>(
        "api/pinouts-internos", { pinouts }, noProjeto(projetoId))).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["internos", componenteId] })
      qc.invalidateQueries({ queryKey: ["catalogo-interno"] })
    },
  })
}

export function usarExcluirInternos(projetoId: number, componenteId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (ids: number[]) => {
      await api.post("api/pinouts-internos/excluir-selecionados", { ids }, noProjeto(projetoId))
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["internos", componenteId] }),
  })
}

// ------------------------------------------------ catálogos do componente

type ComponenteInternoResponse = {
  id: number; componenteId: number; codigo: string; descricao: string; apelido: string; tipo: number; qtd: number
}
type CircuitoInternoResponse = {
  id: number; componenteId: number; descricao: string; apelido: string; tipo: number; qtd: number
}

/**
 * Os dois catálogos internos. Como o de fora, **uma chamada por componente por
 * sessão** — a regra 1 vale igual aqui, e o banco é o mesmo que está no limite.
 */
export function usarComponentesInternos(componenteId: number) {
  const { data, isPending } = useQuery({
    queryKey: ["catalogo-interno", "componentes", componenteId] as const,
    enabled: componenteId > 0,
    ...catalogoPorProjeto,
    queryFn: () => obter<PaginaResponse<ComponenteInternoResponse>>(
      "api/pinouts-internos/componentes", { componenteId, pagina: 1 }),
  })
  const itens: ItemDeCatalogo[] = (data?.itens ?? []).map((c) => ({
    id: c.id, codigo: c.codigo, rotulo: c.descricao || c.codigo, usos: c.qtd,
  }))
  return { itens, carregando: isPending, total: data?.total ?? 0 }
}

export function usarCircuitosInternos(componenteId: number) {
  const { data, isPending } = useQuery({
    queryKey: ["catalogo-interno", "circuitos", componenteId] as const,
    enabled: componenteId > 0,
    ...catalogoPorProjeto,
    queryFn: () => obter<PaginaResponse<CircuitoInternoResponse>>(
      "api/pinouts-internos/circuitos", { componenteId, pagina: 1 }),
  })
  const itens: ItemDeCatalogo[] = (data?.itens ?? []).map((c) => ({
    id: c.id, rotulo: c.descricao, usos: c.qtd,
  }))
  return { itens, carregando: isPending, total: data?.total ?? 0 }
}


// ------------------------------------------------------- alteração em lote

/**
 * As três alterações em lote do pinout interno: observação, fonte e tipo.
 *
 * Menos que as oito do pinout externo, porque do lado de dentro não há cor,
 * chicote nem restrição para alterar. **O desfazer é o mesmo truque**: o
 * endpoint grava um valor só para todos os ids e não devolve os anteriores,
 * mas a grade tem as linhas em mãos — então quem desfaz agrupa por valor
 * anterior e manda uma chamada por valor distinto.
 */
export type CampoDeLoteInterno = "obs" | "arquivo-original" | "tipo"

export function usarLoteInterno(projetoId: number, componenteId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (
      { campo, ids, valor }: { campo: CampoDeLoteInterno; ids: number[]; valor: string | number },
    ) => {
      const corpo = campo === "obs" ? { ids, obs: String(valor) }
        : campo === "arquivo-original" ? { ids, arquivoOriginal: String(valor) }
        : { ids, tipo: Number(valor) }
      await api.post(`api/pinouts-internos/lote/${campo}`, corpo,
        noProjeto(projetoId, { params: { projetoId } }))
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["internos", componenteId] }),
  })
}

// ------------------------------------- editar e excluir os catálogos internos

/**
 * Componente e circuito internos são cadastro, e cadastro se corrige.
 *
 * Sem isto, um nome digitado errado no lançamento (que cria por get-or-create)
 * ficava para sempre: dava para criar e usar, nunca para arrumar. A exclusão
 * existe pelo mesmo motivo — o que foi criado por engano some.
 */
export function usarSalvarCatalogoInterno(
  projetoId: number, componenteId: number, tipo: "componentes" | "circuitos",
) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, dados }: { id: number; dados: Record<string, unknown> }) => {
      await api.put(`api/pinouts-internos/${tipo}/${id}`, dados, noProjeto(projetoId))
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["catalogo-interno", tipo, componenteId] })
      qc.invalidateQueries({ queryKey: ["internos", componenteId] })
    },
  })
}

export function usarExcluirCatalogoInterno(
  projetoId: number, componenteId: number, tipo: "componentes" | "circuitos",
) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`api/pinouts-internos/${tipo}/${id}`, noProjeto(projetoId))
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["catalogo-interno", tipo, componenteId] })
      qc.invalidateQueries({ queryKey: ["internos", componenteId] })
    },
  })
}
